import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Store, Mail, Phone, Building2, Megaphone, Headphones } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "new", label: "Nuova", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  { value: "in_progress", label: "In lavorazione", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  { value: "done", label: "Completata", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  { value: "rejected", label: "Rifiutata", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
];
const statusMeta = (v) => STATUS_OPTIONS.find((s) => s.value === v) || STATUS_OPTIONS[0];

const TYPE_META = {
  marketing: { label: "Marketing", icon: Megaphone, cls: "text-[hsl(var(--primary))]" },
  sales_assistance: { label: "Assistenza vendita", icon: Headphones, cls: "text-[#8a6d1f]" },
};

const TYPE_FILTERS = [
  { value: "", label: "Tutti" },
  { value: "marketing", label: "Marketing" },
  { value: "sales_assistance", label: "Assistenza vendita" },
];

export default function AdminVendorServices() {
  const [data, setData] = useState({ requests: [], total: 0, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");

  const reload = () => {
    setLoading(true);
    const qs = typeFilter ? `?service_type=${typeFilter}` : "";
    api.get(`/admin/vendor/services${qs}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Errore caricamento"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [typeFilter]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/vendor/services/${id}/status`, { status });
      toast.success("Stato aggiornato");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const newCount = data.requests.filter((r) => r.status === "new").length;

  return (
    <div data-testid="admin-vendor-services-page" className="container-xl py-8">
      <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] mb-2">Super Admin · Fornitori</div>
      <h1 className="editorial-heading text-4xl flex items-center gap-3">
        <Store className="h-8 w-8 text-[hsl(var(--primary))]" />Servizi Fornitori
      </h1>
      <p className="text-muted-foreground mt-2 mb-6">
        Richieste di <strong>marketing</strong> e <strong>assistenza alla vendita</strong> inviate dalle aziende.
      </p>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Totale</div>
          <div className="text-2xl font-bold mt-1">{data.total}</div>
        </div>
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Megaphone className="h-3 w-3" />Marketing</div>
          <div className="text-2xl font-bold mt-1 text-[hsl(var(--primary))]">{data.by_type?.marketing || 0}</div>
        </div>
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Headphones className="h-3 w-3" />Assistenza</div>
          <div className="text-2xl font-bold mt-1 text-[#8a6d1f]">{data.by_type?.sales_assistance || 0}</div>
        </div>
        <div className="border border-blue-500/30 bg-blue-500/10 p-4 rounded-sm">
          <div className="text-xs uppercase tracking-wider text-blue-500">Nuove da gestire</div>
          <div className="text-2xl font-bold mt-1 text-blue-500">{newCount}</div>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 mb-6">
        {TYPE_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            data-testid={`vs-filter-${f.value || "all"}`}
            onClick={() => setTypeFilter(f.value)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.15em] rounded-sm border transition-colors ${
              typeFilter === f.value
                ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                : "border-border text-muted-foreground hover:border-[hsl(var(--primary))]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-12">Caricamento...</div>
      ) : data.requests.length === 0 ? (
        <div className="bg-card border border-border p-12 text-center rounded-sm">
          <Store className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <div className="text-lg font-semibold">Nessuna richiesta ancora</div>
          <div className="text-sm text-muted-foreground mt-1">Le richieste servizi dai fornitori appariranno qui.</div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Azienda</TableHead>
                <TableHead>Servizio</TableHead>
                <TableHead>Richiesta</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.requests.map((r) => {
                const meta = statusMeta(r.status);
                const tm = TYPE_META[r.service_type] || TYPE_META.marketing;
                const TIcon = tm.icon;
                return (
                  <TableRow key={r.id} data-testid={`vendor-svc-${r.id}`}>
                    <TableCell className="max-w-[220px]">
                      <div className="font-semibold truncate flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />{r.vendor_company}
                      </div>
                      {r.vendor_email && (
                        <a href={`mailto:${r.vendor_email}`} className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />{r.vendor_email}
                        </a>
                      )}
                      {r.contact_phone && (
                        <a href={`tel:${r.contact_phone}`} className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />{r.contact_phone}
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${tm.cls}`}>
                        <TIcon className="h-4 w-4" />{tm.label}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[320px]">
                      <div className="text-sm text-foreground/80" title={r.message}>{r.message}</div>
                    </TableCell>
                    <TableCell className="font-bold text-[hsl(var(--primary))] whitespace-nowrap">
                      {r.budget != null ? `€${Number(r.budget).toLocaleString("it-IT")}` : "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </TableCell>
                    <TableCell>
                      <Select value={r.status || "new"} onValueChange={(v) => updateStatus(r.id, v)}>
                        <SelectTrigger data-testid={`vendor-svc-status-${r.id}`} className={`h-8 text-xs border ${meta.cls}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
